import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  useTheme,
  alpha,
  Grid,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Work,
  People,
  CheckCircle,
  Add,
  ArrowUpward,
  PostAdd,
  ViewList,
  Phone
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { supabase } from '../../lib/supabaseClient';
import { getAuthUser } from '../../services/authService';
import { useEntity } from '../../context/EntityContext';

interface DashboardStats {
  totalJobs: number;
  activeJobs: number;
  totalApplications: number;
  newApplications: number;
  hiredCount: number;
  conversionRate: number;
}

interface TrendPoint {
  date: string;
  applications: number;
  [key: string]: string | number;
}

interface PipelineSlice {
  name: string;
  value: number;
  color: string;
  [key: string]: string | number;
}

interface TopJob {
  title: string;
  applications: number;
  [key: string]: string | number;
}

const STATUS_COLORS: Record<string, string> = {
  Pending: '#FCD34D',
  Reviewing: '#60A5FA',
  Shortlisted: '#6366F1',
  Rejected: '#FB7185',
  Hired: '#5EEAD4',
};

export const SimpleDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { entity, isSuperAdmin, isLoading: contextLoading } = useEntity();

  useEffect(() => {
    if (!contextLoading && isSuperAdmin) {
      navigate('/admin/entity-analytics', { replace: true });
    }
  }, [isSuperAdmin, contextLoading, navigate]);

  const [stats, setStats] = useState<DashboardStats>({
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    newApplications: 0,
    hiredCount: 0,
    conversionRate: 0,
  });
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [pipeline, setPipeline] = useState<PipelineSlice[]>([]);
  const [topJobs, setTopJobs] = useState<TopJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const user = await getAuthUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      const entityId = entity?.id || user.entityId;

      const { count: totalJobsCount } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .is('deleted_at', null);

      const { count: activeJobsCount } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .eq('status', 'active')
        .is('deleted_at', null);

      const { count: totalApplicationsCount } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .is('deleted_at', null);

      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const { count: newApplicationsCount } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('entity_id', entityId)
        .gte('submitted_at', oneWeekAgo.toISOString())
        .is('deleted_at', null);

      const statusKeys = ['pending', 'reviewing', 'shortlisted', 'rejected', 'hired'] as const;
      const statusCounts: Record<string, number> = {};
      await Promise.all(statusKeys.map(async (s) => {
        const { count } = await supabase
          .from('applications')
          .select('*', { count: 'exact', head: true })
          .eq('entity_id', entityId)
          .eq('status', s)
          .is('deleted_at', null);
        statusCounts[s] = count || 0;
      }));

      const hiredCount = statusCounts.hired || 0;
      const conversionRate = totalApplicationsCount && totalApplicationsCount > 0
        ? Math.round((hiredCount / totalApplicationsCount) * 100 * 10) / 10
        : 0;

      const fourteenDaysAgo = new Date();
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
      fourteenDaysAgo.setHours(0, 0, 0, 0);
      const { data: trendRows } = await supabase
        .from('applications')
        .select('submitted_at')
        .eq('entity_id', entityId)
        .gte('submitted_at', fourteenDaysAgo.toISOString())
        .is('deleted_at', null);

      const trendBuckets: Record<string, number> = {};
      for (let i = 0; i < 14; i++) {
        const d = new Date(fourteenDaysAgo);
        d.setDate(d.getDate() + i);
        const key = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        trendBuckets[key] = 0;
      }
      (trendRows || []).forEach((row: any) => {
        const key = new Date(row.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (key in trendBuckets) {
          trendBuckets[key]++;
        }
      });
      const trendData: TrendPoint[] = Object.entries(trendBuckets).map(([date, applications]) => ({ date, applications }));

      const pipelineData: PipelineSlice[] = [
        { name: 'Pending', value: statusCounts.pending || 0, color: STATUS_COLORS.Pending },
        { name: 'Reviewing', value: statusCounts.reviewing || 0, color: STATUS_COLORS.Reviewing },
        { name: 'Shortlisted', value: statusCounts.shortlisted || 0, color: STATUS_COLORS.Shortlisted },
        { name: 'Rejected', value: statusCounts.rejected || 0, color: STATUS_COLORS.Rejected },
        { name: 'Hired', value: hiredCount, color: STATUS_COLORS.Hired },
      ].filter((slice) => slice.value > 0);

      const { data: jobRows } = await supabase
        .from('jobs')
        .select('title, applications:applications(count)')
        .eq('entity_id', entityId)
        .is('deleted_at', null);

      const jobsWithCounts: TopJob[] = (jobRows || [])
        .map((j: any) => ({
          title: j.title,
          applications: j.applications?.[0]?.count || 0,
        }))
        .filter((j) => j.applications > 0)
        .sort((a, b) => b.applications - a.applications)
        .slice(0, 5);

      setStats({
        totalJobs: totalJobsCount || 0,
        activeJobs: activeJobsCount || 0,
        totalApplications: totalApplicationsCount || 0,
        newApplications: newApplicationsCount || 0,
        hiredCount,
        conversionRate,
      });
      setTrend(trendData);
      setPipeline(pipelineData);
      setTopJobs(jobsWithCounts);
      setLastUpdated(new Date());

    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.id]);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDashboardData, entity?.id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={40} thickness={3} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 2, border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`, boxShadow: 'none' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error Loading Dashboard
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={fetchDashboardData}>
            Retry
          </Button>
        </Paper>
      </Box>
    );
  }

  const formatLastUpdated = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const kpis = [
    {
      label: 'Total Jobs',
      value: stats.totalJobs,
      icon: <Work sx={{ fontSize: 18 }} />,
      delta: {
        text: 'active',
        value: stats.activeJobs,
        positive: stats.activeJobs > 0,
      },
    },
    {
      label: 'Applications',
      value: stats.totalApplications,
      icon: <People sx={{ fontSize: 18 }} />,
      delta: {
        text: 'this week',
        value: stats.newApplications,
        positive: stats.newApplications > 0,
        prefix: '+',
      },
    },
    {
      label: 'Hires',
      value: stats.hiredCount,
      icon: <CheckCircle sx={{ fontSize: 18 }} />,
      delta: {
        text: `${stats.conversionRate}% conversion`,
        value: null as number | null,
        positive: false,
      },
    },
  ];

  const quickActions = [
    { label: 'Post Job', icon: <PostAdd sx={{ fontSize: 22 }} />, to: '/admin/jobs/post' },
    { label: 'Manage Jobs', icon: <ViewList sx={{ fontSize: 22 }} />, to: '/admin/jobs' },
    { label: 'Applications', icon: <People sx={{ fontSize: 22 }} />, to: '/admin/applications' },
    { label: 'Call Screening', icon: <Phone sx={{ fontSize: 22 }} />, to: '/admin/calls' },
  ];

  const cardSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    boxShadow: 'none',
    background: '#FFFFFF',
    transition: 'border-color 0.2s ease',
    '&:hover': {
      borderColor: alpha(theme.palette.primary.main, 0.4),
      transform: 'none',
      boxShadow: 'none',
    },
  };

  const sectionLabel = {
    color: 'text.secondary',
    fontWeight: 600,
    letterSpacing: '0.08em',
    lineHeight: 1,
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5, letterSpacing: '-0.02em' }}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {entity?.name ? `${entity.name} · ` : ''}
            {lastUpdated ? `Updated ${formatLastUpdated(lastUpdated)}` : 'Welcome back'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          component={Link}
          to="/admin/jobs/post"
          disableElevation
          sx={{
            background: theme.palette.primary.main,
            boxShadow: 'none',
            '&:hover': {
              background: theme.palette.primary.dark,
              boxShadow: 'none',
              transform: 'none',
            },
          }}
        >
          Post New Job
        </Button>
      </Stack>

      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {kpis.map((kpi) => (
          <Grid item xs={12} sm={4} key={kpi.label}>
            <Card sx={cardSx}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2.5 }}>
                  <Typography variant="overline" sx={sectionLabel}>
                    {kpi.label}
                  </Typography>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.25,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {kpi.icon}
                  </Box>
                </Stack>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '2.25rem',
                    color: 'text.primary',
                    lineHeight: 1.1,
                    mb: 1.5,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {kpi.value}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  {kpi.delta.positive && kpi.delta.value !== null && (
                    <ArrowUpward sx={{ fontSize: 14, color: theme.palette.success.main }} />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                    {kpi.delta.value !== null && (
                      <Box
                        component="span"
                        sx={{
                          color: kpi.delta.positive ? theme.palette.success.main : 'text.primary',
                          fontWeight: 600,
                          mr: 0.5,
                        }}
                      >
                        {kpi.delta.prefix || ''}{kpi.delta.value}
                      </Box>
                    )}
                    {kpi.delta.text}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} md={7}>
          <Card sx={cardSx}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="overline" sx={sectionLabel}>
                  Applications · Last 14 Days
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {trend.reduce((s, t) => s + t.applications, 0)} total
                </Typography>
              </Stack>
              {trend.length === 0 || trend.every((t) => t.applications === 0) ? (
                <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No applications in the last 14 days</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trend} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                      tickLine={false}
                      axisLine={{ stroke: theme.palette.divider }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: '#FFFFFF',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                        fontSize: '0.8125rem',
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="applications"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      dot={{ r: 3, fill: theme.palette.primary.main }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={cardSx}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="overline" sx={{ ...sectionLabel, display: 'block', mb: 2 }}>
                Pipeline
              </Typography>
              {pipeline.length === 0 ? (
                <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No applications yet</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={pipeline}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pipeline.map((slice) => (
                        <Cell key={slice.name} fill={slice.color} stroke="none" />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: '#FFFFFF',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                        fontSize: '0.8125rem',
                      }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: '0.75rem' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ ...cardSx, mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="overline" sx={sectionLabel}>
              Top Jobs by Applications
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Top {topJobs.length} of {stats.totalJobs}
            </Typography>
          </Stack>
          {topJobs.length === 0 ? (
            <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">No applications received yet</Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(180, topJobs.length * 44)}>
              <BarChart data={topJobs} layout="vertical" margin={{ top: 0, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <YAxis
                  type="category"
                  dataKey="title"
                  tick={{ fontSize: 12, fill: theme.palette.text.primary }}
                  tickLine={false}
                  axisLine={false}
                  width={160}
                />
                <Tooltip
                  cursor={{ fill: alpha(theme.palette.primary.main, 0.05) }}
                  contentStyle={{
                    background: '#FFFFFF',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    fontSize: '0.8125rem',
                  }}
                />
                <Bar dataKey="applications" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Box>
        <Typography variant="overline" sx={{ ...sectionLabel, display: 'block', mb: 2 }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          {quickActions.map((action) => (
            <Grid item xs={6} sm={6} md={3} key={action.label}>
              <Paper
                component={Link}
                to={action.to}
                elevation={0}
                sx={{
                  p: 2.5,
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  background: '#FFFFFF',
                  transition: 'border-color 0.2s ease, background-color 0.2s ease',
                  '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {action.icon}
                </Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'text.primary' }}
                >
                  {action.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};
